import dayjs from 'dayjs';
import {
  TimelineBox,
  TimelineDivider,
  TimelineDividers,
  TimelineTime,
  TimelineWrapper,
  useTimeline,
} from 'planby-mit';

/** Narrowest slot (px) that still fits a time label such as "12:55pm". */
const MIN_SLOT_LABEL_WIDTH = 64;

interface TimelineProps {
  isBaseTimeFormat: boolean;
  isSidebar: boolean;
  dayWidth: number;
  hourWidth: number;
  numberOfHoursInDay: number;
  offsetStartHoursRange: number;
  sidebarWidth: number;
  startDate: string;
  timeSlotInterval: number | null;
}

export function Timeline({
  isBaseTimeFormat,
  isSidebar,
  dayWidth,
  hourWidth,
  numberOfHoursInDay,
  offsetStartHoursRange,
  sidebarWidth,
  startDate,
  timeSlotInterval,
}: TimelineProps) {
  const { time, dividers, formatTime } = useTimeline(
    numberOfHoursInDay,
    isBaseTimeFormat
  );

  const renderDividers = (width: number) =>
    dividers.map((_, index) => <TimelineDivider key={index} width={width} />);

  const renderTime = (index: number) => (
    <TimelineBox key={index} width={hourWidth}>
      <TimelineTime>
        {formatTime(index + offsetStartHoursRange).toLowerCase()}
      </TimelineTime>
      <TimelineDividers>{renderDividers(hourWidth)}</TimelineDividers>
    </TimelineBox>
  );

  const renderSlots = (interval: number) => {
    const slotWidth: number = (hourWidth * interval) / 60;
    const timelineWidth: number = hourWidth * numberOfHoursInDay;
    // the last slot is clamped to the remaining time when the interval does not divide it
    const numberOfSlots: number = Math.ceil(
      (numberOfHoursInDay * 60) / interval
    );
    const firstSlot = dayjs(startDate)
      .startOf('day')
      .add(offsetStartHoursRange, 'hour');
    const timeFormat: string = isBaseTimeFormat ? 'h:mma' : 'HH:mm';

    return Array.from({ length: numberOfSlots }, (_, index) => {
      const width: number = Math.min(
        slotWidth,
        timelineWidth - index * slotWidth
      );

      // a short remainder slot keeps its divider but skips the label so it can't overflow the timeline
      const isLabelVisible: boolean = width >= MIN_SLOT_LABEL_WIDTH;

      return (
        <TimelineBox key={index} width={width}>
          {isLabelVisible && (
            <TimelineTime>
              {firstSlot.add(index * interval, 'minute').format(timeFormat)}
            </TimelineTime>
          )}
          <TimelineDividers>
            <TimelineDivider width={width} />
          </TimelineDividers>
        </TimelineBox>
      );
    });
  };

  return (
    <TimelineWrapper
      dayWidth={dayWidth}
      sidebarWidth={sidebarWidth}
      isSidebar={isSidebar}
    >
      {timeSlotInterval
        ? renderSlots(timeSlotInterval)
        : time.map((_, index) => renderTime(index))}
    </TimelineWrapper>
  );
}
